/**
 * The standalone dashboard page, `/dashboards/:dashboardId` (`specs/M10-D1-dashboards.md` §8.1).
 *
 * This account has **no** standalone dashboards — it has five board views that host widgets —
 * but `create_dashboard` is part of monday's API and the workspace **+** menu offers *Dashboard*
 * `[cap:sandbox-workspace-add-menu.json]`, so the page exists and is the same canvas.
 */
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { PageSkeleton } from '@elizmonday/ui';
import { useTRPC } from '../../lib/trpc';
import { WidgetCanvas } from './WidgetCanvas';
import { useDashboardSource } from './hooks';
import { useMeasureTitles } from './measure-titles';
import './dashboards.css';

export function DashboardPage() {
  const params = useParams();
  const dashboardId = params['dashboardId'] ?? '';
  const trpc = useTRPC();
  const dashboard = useQuery({
    ...trpc.dashboards.get.queryOptions({ id: dashboardId }),
    enabled: /^\d+$/.test(dashboardId),
  });
  const boardIds = dashboard.data?.boardIds ?? [];
  // The canvas needs its boards for two jobs: the settings pickers and the realtime rooms (§6.2).
  const source = useDashboardSource({ kind: 'dashboard', id: dashboardId }, { boardIds });
  const measureTitle = useMeasureTitles(boardIds);

  if (dashboard.isPending) return <PageSkeleton variant="board" />;
  return (
    <div data-testid="dashboard-page" data-dashboard-id={dashboardId}>
      <h1 className="ez-widget-empty-card__title" style={{ padding: '16px 0 0 16px' }} data-testid="dashboard-name">
        {dashboard.data?.name ?? 'Dashboard'}
      </h1>
      <WidgetCanvas source={source} measureTitle={measureTitle} />
    </div>
  );
}
